import { useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import type { Attachment } from '@shared/types';
import { formatFileSize } from '@/lib/format';

/**
 * Full-size image viewer with keyboard navigation.
 *
 * The legacy app had no lightbox — clicking an image navigated away to the raw
 * file URL, losing your place. This keeps you on the task.
 */
export function Lightbox({
  images,
  current,
  onChange,
  onClose,
}: {
  images: Attachment[];
  current: Attachment;
  onChange: (image: Attachment) => void;
  onClose: () => void;
}) {
  const index = images.findIndex((i) => i.id === current.id);

  const step = useCallback(
    (delta: number) => {
      if (images.length < 2) return;
      // Wrap around so arrow keys never dead-end.
      const next = images[(index + delta + images.length) % images.length];
      if (next) onChange(next);
    },
    [images, index, onChange],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
    }
    window.addEventListener('keydown', onKey);

    // Stop the page behind the overlay from scrolling.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose, step]);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/90"
      role="dialog"
      aria-modal="true"
      aria-label={current.fileName}
    >
      <header className="flex items-center gap-3 px-4 py-3 text-white">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{current.fileName}</p>
          <p className="text-xs text-white/60">
            {formatFileSize(current.fileSize)}
            {images.length > 1 && ` · ${index + 1} von ${images.length}`}
          </p>
        </div>
        <a
          href={current.url}
          download={current.fileName}
          className="grid size-9 place-items-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
          aria-label="Herunterladen"
        >
          <Download className="size-4" />
        </a>
        <button
          onClick={onClose}
          className="grid size-9 place-items-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
          aria-label="Schließen"
        >
          <X className="size-5" />
        </button>
      </header>

      {/* Clicking the backdrop closes; clicking the image itself must not. */}
      <div
        className="flex min-h-0 flex-1 items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {images.length > 1 && (
          <button
            onClick={() => step(-1)}
            className="mr-2 grid size-10 shrink-0 place-items-center rounded-full bg-white/10
                       text-white hover:bg-white/20"
            aria-label="Vorheriges Bild"
          >
            <ChevronLeft className="size-5" />
          </button>
        )}

        <img
          src={current.url}
          alt={current.fileName}
          className="max-h-full max-w-full object-contain"
        />

        {images.length > 1 && (
          <button
            onClick={() => step(1)}
            className="ml-2 grid size-10 shrink-0 place-items-center rounded-full bg-white/10
                       text-white hover:bg-white/20"
            aria-label="Nächstes Bild"
          >
            <ChevronRight className="size-5" />
          </button>
        )}
      </div>
    </div>
  );
}
